import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "minimal-shared";
import { Paper, Typography, Box, TextField, Button, Tooltip, Snackbar, Alert, Drawer, Stack, IconButton, Tabs, Tab, Chip, InputAdornment, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Select, MenuItem, Skeleton, Grid2, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Autocomplete } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomerSearch, { formatCustomerDisplayLabel } from "../../Epos/component/CustomerSearch";
import CartTable from "../../Epos/component/CartTable";
import QuickOrderProductSearch from "../component/QuickOrderProductSearch";
import SageCustomerMemosPopup from "../component/SageCustomerMemosPopup";
import { ConfirmDialog } from "../../../common/confirm-dialog";
import { getCartIncompleteOrders, getCustomerList, getProductsList, placeOrder, fetchProductStockMapForCartLines } from "../../../../data/epos";
import { useEnhancedCartLogic } from "../../Epos/hooks/useEnhancedCartLogic";
import { PLATFORMS, clearCartByCustomer, remove_promotions } from "../../../../data/cart";
import { prepareProductForCart, isMeatsProduct, parseQty, allowsDecimalQty, cartLineHasPromoOfferQtyControls, cartLineMissingDiscountComment } from "../../Epos/utils/cartCalculations";
import { shouldSkipTelecallerStockLimit, getTelecallerProductDisplayName } from "../../Epos/utils/telecallerStockLimit";
import {
  syncCartQtyValidationMap,
  computeCartQuantityChange,
  applyCartQtyValidationResult,
  validateCartStockBeforePlaceOrder,
} from "../../Epos/utils/cartQtyValidation";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import {
  getCartPromotions,
  getPromotions,
  changePromotion,
  resolvePreviousPromotionIdForChange,
  collectProductIdsSharingPromotionWithLine,
  buildCartLinePromotionsFallback,
  resolveFreshCartLineForPromotionPopup,
  resolveCartLineSlabId,
} from "../../../../data/promotions";
import config, { PREFIX, retainsCartDiscountFocus } from "../../../common/Config";
import ComingSoonImage from "../../../../assets/img/coming-soon.png";
import PromotionDetailsDrawer from "../../Promotion/components/promo-details-drawer";
import PromotionGetProductsDialog from "../../../common/PromotionGetProductsDialog";
import ProductLinePromotionsDialog from "../../../common/ProductLinePromotionsDialog";
import SalesStockLimitDialog from "../../common/SalesStockLimitDialog";
import {
  validateMaximumOrderQuantityForCartAdd,
  validateMaximumOrderQuantityForLineQty,
  mergeOrderLimitFieldsFromCartLines,
  getRequestedCartLineQuantity,
} from "../../common/maximumOrderQuantity";
import { findMergeableCartLineForProductAdd, getCartItemLookupMap, isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";
import { getStockCapForCartLineItem, isQuantityWithinLineStock } from "../../Promotion/utils/promotionLineStock";
import NumericKeypadQuickOrder from "../../../common/NumericKeypadQuickOrder";
import productSplitService from "../../../../services/ProductSplitService";
import { getCompanySettings, resolveCheckStockBeforeAddToCart, resolveCustomerApiEncrypt } from "../../../../utils/companySettingsCache";
import { buildProductSearchIndexUrl } from "../../../../utils/productSearchUrls";
import { isInsideModalDialog } from "../../Epos/utils/cartTableKeyboard";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../../../utils/customerCrypto";
import { Icon } from "@iconify/react";
import "./quick-order-toolbar.css";
const EMPTY_PRODUCT_OPTIONS = [];
const ProductImageWithFallback = ({ product, productImage }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <Box
      component="img"
      src={productImage && !imageError ? productImage : ComingSoonImage}
      alt={typeof product === 'object' ? (product.product_name || 'Product') : 'Product'}
      onError={() => setImageError(true)}
      sx={{
        width: 80,
        height: 80,
        borderRadius: '8px',
        objectFit: 'cover',
        flexShrink: 0,
        bgcolor: '#F5F5F5'
      }}
    />
  );
};

const QuickOrder = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sageMemosPopupOpen, setSageMemosPopupOpen] = useState(false);
  const [teleCallerOptions, setTeleCallerOptions] = useState([]);
  const [selectedTeleCaller, setSelectedTeleCaller] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [totalInvDiscPercent, setTotalInvDiscPercent] = useState(0);
  const [totalInvDiscType, setTotalInvDiscType] = useState("percentage");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error"
  });
  const [deleteItemDialog, setDeleteItemDialog] = useState({
    open: false,
    itemId: null
  });
  const [openIncompleteOrderDialog, setOpenIncompleteOrderDialog] = useState(false);
  const [customerChangeDialog, setCustomerChangeDialog] = useState({
    open: false,
    newCustomer: null,
  });
  const [pendingCustomerAfterSave, setPendingCustomerAfterSave] = useState(null);
  const [promotionsModalOpen, setPromotionsModalOpen] = useState(false);
  const [productLinePromotionsCtx, setProductLinePromotionsCtx] = useState(null);
  const [promotionDrawerOpen, setPromotionDrawerOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [newlyAddedProductId, setNewlyAddedProductId] = useState(null);
  const [promoProductSwapOpen, setPromoProductSwapOpen] = useState(false);
  const [promoProductSwapCtx, setPromoProductSwapCtx] = useState(null);
  const [quantityKeypadOpen, setQuantityKeypadOpen] = useState(false);
  const [selectedProductForQuantity, setSelectedProductForQuantity] = useState(null);
  const [keypadError, setKeypadError] = useState("");
  const [selectedOrderType, setSelectedOrderType] = useState("Box");
  const [shouldCheckStockBeforeAddToCart, setShouldCheckStockBeforeAddToCart] = useState(true);
  const productSearchApiBase = useMemo(() => {
    const indexUrl = buildProductSearchIndexUrl(config.API_URL);
    return indexUrl.replace(/\/api\/products\/search-index\/?$/i, "");
  }, []);
  const isPromotionOperationRef = useRef(false);
  const [telecallerStockDialog, setTelecallerStockDialog] = useState({
    open: false,
    productName: "",
    productCode: "",
    maxQty: 0,
    message: "",
    plainTitle: undefined,
  });
  const customerSearchRef = useRef(null);
  const quickProductSearchRef = useRef(null);
  const selectedCustomerIdRef = useRef(null);
  const keypadOpeningRef = useRef(false);
  const quantityKeypadOpenRef = useRef(false);
  const lastCartAddProductRef = useRef({ name: "", code: "" });
  const cartQtyValidationRef = useRef(new Map());
  const addingProductFromSearchRef = useRef(false);
  const productSearchFocusTimersRef = useRef([]);
  const newlyAddedProductIdRef = useRef(null);
  const qtyFocusAfterAddTimersRef = useRef([]);
  const skipQtyAutoFocusRef = useRef(false);

  const debouncedCustomerSearch = useDebounce(customerSearchInput, 500);
  const tillUserId = localStorage.getItem('tillUserId');

  const isRhp = useMemo(() => {
    const accessToken = localStorage.getItem('accessToken');
    return accessToken === 'rhp' || accessToken === 'hp';
  }, []);
  const isTelecaller = useMemo(
    () => localStorage.getItem("user_role") === "20",
    []
  );
  const keepQtyFocusAfterAdd = isTelecaller || isRhp || PREFIX === "rhp";
  const clearProductSearchFocusTimers = useCallback(() => {
    productSearchFocusTimersRef.current.forEach((id) => window.clearTimeout(id));
    productSearchFocusTimersRef.current = [];
  }, []);
  const clearQtyFocusAfterAddTimers = useCallback(() => {
    qtyFocusAfterAddTimersRef.current.forEach((id) => window.clearTimeout(id));
    qtyFocusAfterAddTimersRef.current = [];
  }, []);
  const scheduleFocusNewlyAddedQty = useCallback((productId) => {
    skipQtyAutoFocusRef.current = false;
    newlyAddedProductIdRef.current = productId ? String(productId) : null;
    if (typeof document !== "undefined") {
      document.documentElement.removeAttribute("data-qo-qty-to-search");
    }
    clearQtyFocusAfterAddTimers();
    clearProductSearchFocusTimers();
    quickProductSearchRef.current?.blur?.();
    const attempt = () => {
      if (skipQtyAutoFocusRef.current) return;
      const id = newlyAddedProductIdRef.current;
      if (!id) return;
      const scoped = document.querySelector(
        `[data-qty-product-id="${id}"]:not([data-qty-offer-line="1"]) input`
      ) || document.querySelector(`[data-qty-product-id="${id}"] input`);
      const fallback = document.querySelector("input[data-inline-qty-key]");
      const el = scoped || fallback;
      if (!el) return;
      if (document.activeElement === el) {
        clearQtyFocusAfterAddTimers();
        return;
      }
      el.focus();
      if (typeof el.select === "function") {
        el.select();
      }
    };
    attempt();
    [80, 200].forEach((ms) => {
      qtyFocusAfterAddTimersRef.current.push(window.setTimeout(attempt, ms));
    });
  }, [clearQtyFocusAfterAddTimers, clearProductSearchFocusTimers]);
  selectedCustomerIdRef.current = selectedCustomer?.id ?? null;
  quantityKeypadOpenRef.current = quantityKeypadOpen;

  useEffect(() => {
    let isMounted = true;

    const loadCompanySettings = async () => {
      const settings = await getCompanySettings();
      if (!isMounted) return;
      setShouldCheckStockBeforeAddToCart(resolveCheckStockBeforeAddToCart(settings));
    };

    loadCompanySettings();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTeleCallerOptions = async () => {
      const accessToken = localStorage.getItem("accessToken");
      try {
        const settings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(settings);
        const body = new FormData();
        if (useEncrypt) {
          const ep = encryptPayload({});
          if (ep) {
            body.append("encrypted_payload", ep);
          }
        }
        const response = await fetch(`${config.API_URL}/getteleSales`, {
          method: "POST",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
          body: body.entries().next().done ? undefined : body,
        });
        const raw = await response.json();
        const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
        if (!isMounted) return;
        const formattedOptions = (data.data || []).map((item) => ({
          value: item.id,
          label: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
        }));
        setTeleCallerOptions(formattedOptions);
      } catch (error) {
        if (isMounted) setTeleCallerOptions([]);
      }
    };

    loadTeleCallerOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const showSnackbar = useCallback((message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const focusQuickProductSearch = useCallback((immediate = false) => {
    const run = () => {
      if (!selectedCustomerIdRef.current) return;
      if (quantityKeypadOpenRef.current) return;
      quickProductSearchRef.current?.focus?.(true);
    };
    if (immediate) {
      run();
      return;
    }
    requestAnimationFrame(() => {
      setTimeout(run, 50);
    });
  }, []);

  const focusProductSearchAfterQtyEnter = useCallback(() => {
    skipQtyAutoFocusRef.current = true;
    newlyAddedProductIdRef.current = null;
    setNewlyAddedProductId(null);
    clearQtyFocusAfterAddTimers();
    clearProductSearchFocusTimers();
    const run = () => {
      if (!selectedCustomerIdRef.current) return;
      if (quantityKeypadOpenRef.current) return;
      if (!skipQtyAutoFocusRef.current) return;
      quickProductSearchRef.current?.focus?.(true);
    };
    run();
    requestAnimationFrame(() => {
      run();
      productSearchFocusTimersRef.current.push(window.setTimeout(run, 50));
      productSearchFocusTimersRef.current.push(window.setTimeout(run, 150));
      productSearchFocusTimersRef.current.push(window.setTimeout(run, 300));
    });
  }, [clearProductSearchFocusTimers, clearQtyFocusAfterAddTimers]);

  /** Avoid stealing focus from cart +/- (e.g. repeated Enter on increment) while still refocusing search from other flows. */
  const refocusQuickProductSearchUnlessCartQtyFocused = useCallback(
    (immediate = false) => {
      if (typeof document !== "undefined") {
        const active = document.activeElement;
        if (active instanceof Element) {
          if (
            active.closest("[data-cart-qty-scope]") ||
            active.closest("[data-cart-row]") ||
            (retainsCartDiscountFocus && active.closest("[data-cart-discount-input]"))
          ) {
            return;
          }
        }
      }
      focusQuickProductSearch(immediate);
    },
    [focusQuickProductSearch]
  );

  const onCartErrorMessage = useCallback((message, severity = "error") => {
    const msg = String(message || "").trim();
    const isStockLimitError =
      /out of stock|insufficient|exceeds?\b|exceeded|not enough|unavailable|inventory|left in stock|only\s+\d+/i.test(
        msg
      ) ||
      /available\s+quantity|stock\s+limit|maximum\s+quantity|qty\s+reached|quantity\s+reached|left in stock|max(imum)?\s+(qty|quantity|available)|cannot\s+purchase\s+more\s+than/i.test(
        msg
      );
    if (isStockLimitError) {
      const p = lastCartAddProductRef.current;
      const isPurchaseLimitExceeded = /cannot\s+purchase\s+more\s+than/i.test(msg);
      setTelecallerStockDialog({
        open: true,
        productName: p?.name || "",
        productCode: p?.code || "",
        maxQty: undefined,
        message: msg || "Stock limit reached",
        plainTitle: isPurchaseLimitExceeded
          ? "Purchase Limit Exceeded"
          : "Stock limit reached",
      });
      return;
    }
    showSnackbar(message, severity);
  }, [showSnackbar]);

  const {
    cartItems,
    cartTotals,
    isLoadingCart,
    isFetchingCart,
    isCartMutating,
    addProductToCart,
    handleQuantityChange,
    handleTypeChange,
    handlePriceChange,
    handleDiscountChange,
    handleDiscountCommentChange,
    handleDeleteItem,
    handleClearCart,
    getItemDetails,
    isItemPending,
    refetchCart,
    bundleDiscounts = [],
    bundleLineDiscountByKey = null,
  } = useEnhancedCartLogic(
    selectedCustomer?.id,
    totalInvDiscPercent,
    PLATFORMS.TELE_CALLING,
    totalInvDiscType,
    onCartErrorMessage,
    isTelecaller
  );

  const cartItemsRef = useRef(cartItems);
  cartItemsRef.current = cartItems;
  const cartItemLookup = useMemo(() => getCartItemLookupMap(cartItems), [cartItems]);
  const bundleDiscountsRef = useRef(bundleDiscounts);
  bundleDiscountsRef.current = bundleDiscounts;

  useEffect(() => {
    cartQtyValidationRef.current = syncCartQtyValidationMap(cartItems);
  }, [cartItems]);

  const { data: customerList, isLoading: isLoadingCustomerList } = useQuery({
    queryKey: ["customerList", debouncedCustomerSearch, tillUserId],
    queryFn: () => getCustomerList({ pattern: debouncedCustomerSearch }),
    enabled: debouncedCustomerSearch.length >= 3,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });

  const { data: productList, isLoading: isLoadingProductList } = useQuery({
    queryKey: ["productList", "searchIndexLocalV3", selectedCustomer?.id, tillUserId],
    queryFn: () => getProductsList({
      pattern: "",
      customer_id: selectedCustomer?.id,
      platform: PLATFORMS.TELE_CALLING,
      apiBaseUrl: productSearchApiBase,
      useSearchIndex: true,
      returnAllIndex: true,
      forceRefresh: true,
    }),
    enabled: !!selectedCustomer?.id,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });


  const { data: incompleteOrdersData, refetch: refetchIncompleteOrders } = useQuery({
    queryKey: ["incompleteOrders", PLATFORMS.TELE_CALLING],
    queryFn: () =>
      getCartIncompleteOrders({ channel_id: PLATFORMS.TELE_CALLING }),
    enabled: openIncompleteOrderDialog,
    staleTime: 0,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });

  const placeOrderMutation = useMutation({
    mutationFn: placeOrder,
    onSuccess: async (data) => {
      const nextCustomerAfterSave = pendingCustomerAfterSave;
      if (data.success) {
        showSnackbar(data.message || "Order placed successfully", "success");
        const currentCustomerId = selectedCustomer?.id;
        if (currentCustomerId) {
          if (cartItems && cartItems.length > 0) {
            cartItems.forEach(item => {
              if (item.product_id) {
                productSplitService.clearProductType(currentCustomerId, item.product_id);
              }
            });
          }
          await clearCartByCustomer(currentCustomerId, PLATFORMS.TELE_CALLING);
        }
        setTotalInvDiscPercent(0);
        if (nextCustomerAfterSave) {
          setSelectedCustomer(nextCustomerAfterSave);
          setCustomerSearchInput(formatCustomerDisplayLabel(nextCustomerAfterSave));
          setPendingCustomerAfterSave(null);
        } else {
          setSelectedCustomer(null);
          setCustomerSearchInput("");
        }
        queryClient.invalidateQueries({ queryKey: ["cartList"] });
        queryClient.invalidateQueries({ queryKey: ["productList"] });
        queryClient.invalidateQueries({
          queryKey: ["incompleteOrders", PLATFORMS.TELE_CALLING],
        });
        if (nextCustomerAfterSave) {
          setTimeout(() => {
            quickProductSearchRef.current?.focus?.(true);
          }, 100);
        }
      } else {
        setPendingCustomerAfterSave(null);
        showSnackbar(data.message || "Failed to place order", "error");
        focusQuickProductSearch();
      }
    },
    onError: (error) => {
      setPendingCustomerAfterSave(null);
      showSnackbar("Failed to place order. Please try again.", "error");
      focusQuickProductSearch();
    },
  });


  const clearCartMutation = useMutation({
    mutationFn: ({ customerId, platform }) => clearCartByCustomer(customerId, platform),
    onSuccess: async (data) => {
      if (!data?.success) {
        showSnackbar(data?.message || "Failed to clear cart", "error");
        return;
      }

      queryClient.removeQueries({
        queryKey: ['cartList'],
        exact: false
      });

      queryClient.removeQueries({
        queryKey: ['cartPromotions'],
        exact: false
      });

      await refetchCart();
      queryClient.invalidateQueries({
        queryKey: ["incompleteOrders", PLATFORMS.TELE_CALLING],
      });
      showSnackbar(data?.message || "Cart cleared successfully", "success");
      focusQuickProductSearch();
    },
    onError: () => {
      showSnackbar("Failed to clear cart", "error");
      focusQuickProductSearch();
    }
  });

  const removePromotionMutation = useMutation({
    mutationFn: ({ customerId, promotionId }) =>
      remove_promotions({}, customerId, PLATFORMS.TELE_CALLING, promotionId),
    onSuccess: async (data, variables) => {
      if (data.success) {
        const storageKey = `cartManualOverrides_${variables.customerId}`;
        if (typeof window !== "undefined" && storageKey) {
          localStorage.removeItem(storageKey);
        }

        queryClient.removeQueries({
          queryKey: ['cartList'],
          exact: false
        });

        queryClient.removeQueries({
          queryKey: ['cartPromotions'],
          exact: false
        });

        queryClient.invalidateQueries({
          queryKey: ['cartList'],
          exact: false
        });

        await new Promise(resolve => setTimeout(resolve, 300));

        await refetchCart();

        showSnackbar(`Promotion removed successfully`, "success");
        focusQuickProductSearch();
      } else {
        showSnackbar(data.message || "Failed to remove promotion", "error");
        focusQuickProductSearch();
      }
    },
    onError: (error) => {
      console.error("Error removing promotion:", error);
      showSnackbar("Failed to remove promotion. Please try again.", "error");
      focusQuickProductSearch();
    },
  });

  const refreshCartThenPromotions = useCallback(async () => {
    if (!selectedCustomer?.id) {
      return;
    }
    try {
      await refetchCart();
    } catch {
    }
  }, [selectedCustomer?.id, refetchCart]);

  const customerOptions = customerList?.customer_details || [];
  const productOptionsRaw = useMemo(
    () =>
      productList?.product_details ||
      productList?.product_detials ||
      productList?.data?.product_details ||
      productList?.data?.product_detials ||
      EMPTY_PRODUCT_OPTIONS,
    [productList]
  );

  useEffect(() => {
    const savedCustomer = sessionStorage.getItem('quickOrder_selectedCustomer');
    const savedCustomerSearch = sessionStorage.getItem('quickOrder_customerSearch');
    if (savedCustomer) {
      try {
        const customer = JSON.parse(savedCustomer);
        setSelectedCustomer(customer);
        if (savedCustomerSearch) {
          setCustomerSearchInput(savedCustomerSearch);
        }
      } catch (error) {
        console.error('Error loading saved customer:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      sessionStorage.setItem('quickOrder_selectedCustomer', JSON.stringify(selectedCustomer));
      if (customerSearchInput) {
        sessionStorage.setItem('quickOrder_customerSearch', customerSearchInput);
      }
    } else {
      sessionStorage.removeItem('quickOrder_selectedCustomer');
      sessionStorage.removeItem('quickOrder_customerSearch');
    }
  }, [selectedCustomer, customerSearchInput]);

  useEffect(() => {
    let backup;
    if (!selectedCustomer?.id) {
      setTotalInvDiscPercent(0);
    } else {
      focusQuickProductSearch(true);
      backup = window.setTimeout(() => {
        focusQuickProductSearch(true);
      }, 120);
    }
    setSelectedProduct(null);
    quickProductSearchRef.current?.clear?.();
    return () => {
      if (backup != null) clearTimeout(backup);
    };
  }, [selectedCustomer?.id, focusQuickProductSearch]);

  useEffect(() => {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem("quickOrder_selectedCustomer")) {
      return;
    }
    if (customerSearchRef.current?.focusInput) {
      const timer = setTimeout(() => {
        if (selectedCustomerIdRef.current) return;
        customerSearchRef.current?.focusInput?.();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const tryFocusProductSearchFromCart = (event) => {
      const t = event.target;
      if (!(t instanceof Element)) return;
      if (isInsideModalDialog(t)) return;
      if (t.tagName === "SELECT") return;
      if (!t.closest("[data-cart-qty-scope]") && !t.closest("[data-cart-row]")) return;
      if (!selectedCustomer?.id) return;
      event.preventDefault();
      focusQuickProductSearch(true);
    };

    const handleKeyDown = (event) => {
      if (event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable) {
        return;
      }

      if (event.key === '/' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        if (customerSearchRef.current?.focusInput) {
          customerSearchRef.current.focusInput();
        }
      } else if (event.key === 'a' || event.key === 'A') {
        event.preventDefault();
        if (selectedCustomer?.id) {
          quickProductSearchRef.current?.openModal?.();
        } else {
          showSnackbar("Please select a customer first", "warning");
        }
      } else if (event.key === "Escape") {
        tryFocusProductSearchFromCart(event);
      } else if (event.key === "/" && !event.ctrlKey && !event.metaKey) {
        tryFocusProductSearchFromCart(event);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCustomer, showSnackbar, focusQuickProductSearch]);

  useEffect(() => {
    if (selectedProduct && selectedCustomer?.id && !quantityKeypadOpen && !selectedProductForQuantity) {
      const productId = String(selectedProduct.id || "");
      const splitValue = String(selectedProduct.split || "0").trim();

      let orderType = selectedProduct.order_type;
      if (!orderType) {
        if (splitValue === "1") {
          orderType = "Piece";
        } else if (splitValue === "2") {
          const storedOrderType = productSplitService.getDefaultOrderType(selectedCustomer.id, productId);
          orderType = storedOrderType || "Box";
        } else {
          orderType = "Box";
        }
      }

      const existingItem = findMergeableCartLineForProductAdd(
        cartItemLookup,
        productId,
        orderType
      );

      if (existingItem) {
        const currentQty = parseInt(existingItem.qty) || 0;
        if (currentQty >= 9999) {
          showSnackbar("Quantity cannot exceed 9999. Please reduce quantity before adding more.", "warning");
          setSelectedProduct(null);
          quickProductSearchRef.current?.clear?.();
          return;
        }
        if (keepQtyFocusAfterAdd) {
          setSelectedProduct(null);
          quickProductSearchRef.current?.clear?.();
          setNewlyAddedProductId(productId);
          scheduleFocusNewlyAddedQty(productId);
          return;
        }
      }

      const productWithOrderType = {
        ...selectedProduct,
        order_type: orderType
      };

      const preparedProduct = prepareProductForCart(productWithOrderType);
      const incomingQty = getRequestedCartLineQuantity(preparedProduct);
      const purchaseLimit = validateMaximumOrderQuantityForCartAdd(
        productWithOrderType,
        cartItems,
        incomingQty
      );
      if (!purchaseLimit.allowed) {
        setTelecallerStockDialog({
          open: true,
          productName: getTelecallerProductDisplayName(productWithOrderType),
          productCode: selectedProduct?.product_code || selectedProduct?.code || "",
          maxQty: undefined,
          message: purchaseLimit.message || "",
          plainTitle: "Purchase Limit Exceeded",
        });
        setSelectedProduct(null);
        quickProductSearchRef.current?.clear?.();
        return;
      }
      if (addProductToCart && typeof addProductToCart === 'function') {
        lastCartAddProductRef.current = {
          name: selectedProduct?.product_name || selectedProduct?.name || "",
          code: selectedProduct?.product_code || selectedProduct?.code || "",
        };
        addProductToCart(preparedProduct);
      }
      setNewlyAddedProductId(productId);
      setSelectedProduct(null);
      quickProductSearchRef.current?.clear?.();

      if (keepQtyFocusAfterAdd) {
        scheduleFocusNewlyAddedQty(productId);
      } else {
        focusQuickProductSearch();
      }
    }
  }, [selectedProduct, selectedCustomer, addProductToCart, cartItems, cartItemLookup, showSnackbar, quantityKeypadOpen, selectedProductForQuantity, focusQuickProductSearch, keepQtyFocusAfterAdd, scheduleFocusNewlyAddedQty]);

  useEffect(() => {
    const handleCartUpdate = async (event) => {
      if (event.detail?.customerId && selectedCustomer?.id === event.detail.customerId) {
        await refreshCartThenPromotions();
      }
    };

    window.addEventListener('quickOrderCartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('quickOrderCartUpdated', handleCartUpdate);
    };
  }, [selectedCustomer?.id, refreshCartThenPromotions]);


  const generateUniqueId = () => {
    return `id_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const buildOrderPayload = () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const userId = userData.id || "";
    const today = new Date().toISOString().split('T')[0];
    const jwtToken = localStorage.getItem("token");
    const accessToken = localStorage.getItem("accessToken");
    const token = jwtToken || accessToken;

    const saleDetails = cartItems.map((item, index) => {
      const orderType = item.order_type || item.type || "Box";
      const price = parseFloat(item.price) || 0;
      const isFreeItem = (item.promo_type === "FREE" || item.promoType === "FREE") || (item.is_promoted === "1" && price === 0);
      const unitPrice = isFreeItem ? 0 : (parseFloat(item.unit_price) || parseFloat(item.price) || 0);
      const qty = parseInt(item.qty) || 0;
      const vatRate = parseFloat(item.tax_rate || item.rate) || 0;
      const vatAmount = parseFloat(item.vat || item.tax) || 0;

      // Get discount_type to determine how to calculate discount
      const rawDiscountType = item.discount_type || item.manualOverrides?.discount_type || item.productWiseDiscountType || "amount";
      const normalizedDiscountType = String(rawDiscountType).trim().toLowerCase();
      const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

      const subtotal = price * qty;
      const totalVat = vatAmount * qty;

      let discountPercent = 0;
      let discountAmount = 0;
      let discountedSubtotal = subtotal;
      let discountedVat = totalVat;

      const itemDiscount = parseFloat(item.discount || item.manualOverrides?.discount || 0);

      if (isFixedDiscount) {
        discountAmount = itemDiscount;
        if (!Number.isFinite(discountAmount) || discountAmount < 0) {
          discountAmount = 0;
        }
        discountPercent = subtotal > 0 ? (discountAmount / subtotal) * 100 : 0;
        discountPercent = Math.min(discountPercent, 100);
        discountedSubtotal = Math.max(0, subtotal);
        discountedVat = totalVat * (discountedSubtotal / subtotal);
      } else {
        discountPercent = itemDiscount;
        if (!Number.isFinite(discountPercent) || discountPercent < 0) {
          discountPercent = 0;
        }
        discountPercent = Math.min(discountPercent, 100);
        const discountMultiplier = 1 - discountPercent / 100;
        discountedSubtotal = subtotal * discountMultiplier;
        discountedVat = totalVat * discountMultiplier;
        discountAmount = subtotal - discountedSubtotal;
      }

      const total = discountedSubtotal + discountedVat;

      const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;
      const promoDiscountPercent = isPromoted ? parseFloat(item.promo_discount_percentage || item.promos_disc_percentage || item.discount_percentage || 0) : 0;
      const promoDiscountAmount = isPromoted && promoDiscountPercent > 0 ? (discountedSubtotal * promoDiscountPercent / 100) : 0;
      const quantityType = (item.order_type || "Box").toLowerCase();
      const isContractPriceApplied = item.isContractPriceApplied === "Y" ? "Y" : "N";

      // Get discount_type from item (already merged from manualOverrides in baseCartItems) for API
      let discountType = "percentage";
      if (rawDiscountType !== undefined && rawDiscountType !== null && rawDiscountType !== "") {
        if (normalizedDiscountType === "fixed" || normalizedDiscountType === "£") {
          discountType = "amount";
        } else if (normalizedDiscountType === "percentage") {
          discountType = "percentage";
        } else if (normalizedDiscountType === "amount") {
          discountType = "amount";
        }
      }

      // Get discount_value based on discount_type
      let discountValue = "0.00";
      if (discountType === "amount") {
        // For amount type, use the discount amount directly
        discountValue = discountAmount.toFixed(2);
      } else {
        // For percentage type, use the percentage value
        discountValue = discountPercent.toFixed(2);
      }

      return {
        product_id: String(item.product_id || ""),
        product_code: item.code || item.product_code || "",
        product_name: item.name || item.product_name || "",
        unit_price: unitPrice.toFixed(2),
        quantity: qty,
        quantityType: quantityType,
        productWiseDiscountPercentage: discountPercent.toFixed(2),
        productWiseDiscountAmount: discountAmount.toFixed(2),
        discount_type: discountType,
        discount_value: discountValue,
        discount_comment:
          !isCartLineOfferDisplayRole(item) && itemDiscount > 0.0001
            ? item.discount_comment || item.manualOverrides?.discount_comment || ""
            : "",
        subTotal: discountedSubtotal.toFixed(2),
        vat: discountedVat.toFixed(2),
        isPromoted: isPromoted ? 1 : 0,
        promo_type: item.promo_type || (isPromoted ? "promo" : "Buy"),
        promo_id: item.promo_id || "",
        promoDiscountPercentage: isPromoted ? promoDiscountPercent.toFixed(2) : "0.00",
        promoDiscountAmount: isPromoted ? promoDiscountAmount.toFixed(2) : "0.00",
        total: total.toFixed(2),
        isContractPriceApplied: isContractPriceApplied
      };
    });

    const subtotal = cartTotals?.subtotal || 0;
    const vat = cartTotals?.vat || 0;
    const totalBeforeDiscount = cartTotals?.totalBeforeDiscount || 0;
    const grandTotal = cartTotals?.grandTotal || 0;

    let invoiceWiseDiscountPercentage = "0.00";
    let invoiceWiseDiscountAmount = "0.00";

    if (totalInvDiscType === "percentage") {
      invoiceWiseDiscountPercentage = (parseFloat(totalInvDiscPercent) || 0).toFixed(2);
      invoiceWiseDiscountAmount = totalBeforeDiscount > 0
        ? ((totalBeforeDiscount * parseFloat(totalInvDiscPercent || 0)) / 100).toFixed(2)
        : "0.00";
    } else if (totalInvDiscType === "fixed") {
      invoiceWiseDiscountPercentage = totalBeforeDiscount > 0
        ? (((parseFloat(totalInvDiscPercent) || 0) / totalBeforeDiscount) * 100).toFixed(2)
        : "0.00";
      invoiceWiseDiscountAmount = (parseFloat(totalInvDiscPercent) || 0).toFixed(2);
    }

    const bundleIds = (bundleDiscounts || [])
      .map((b) => b.bundle_id != null ? Number(b.bundle_id) : null)
      .filter((id) => id != null && Number.isFinite(id));

    return {
      platform: "5",
      channels: PLATFORMS.TELE_CALLING,
      authorization: `Bearer ${token}`,
      user_id: userId,
      product_count: cartItems.length,
      customer_id: String(selectedCustomer.id || ""),
      customer_address: selectedCustomer.address || "",
      totalAmount: grandTotal.toFixed(2),
      totalVAT: vat.toFixed(2),
      paymentMode: "",
      staff_note: "",
      note_for_driver: "",
      invoiceWiseDiscountPercentage: invoiceWiseDiscountPercentage,
      invoiceWiseDiscountAmount: invoiceWiseDiscountAmount,
      invoiceWiseDiscountType: totalInvDiscType,
      discountAmount: "",
      promotionId: "",
      promotionSubType: "",
      collect_amount: "0.00",
      uniqueOrderId: generateUniqueId(),
      chequeNo: "",
      chequeDate: "",
      latitude: "",
      longitude: "",
      address: "",
      product_discount: "0.00",
      status: "Incomplete",
      sales_id: "",
      tele_sales_id: selectedTeleCaller?.value ? String(selectedTeleCaller.value) : "",
      reference_no: 0,
      date: today,
      sale_details: saleDetails,
      ...(bundleIds.length > 0 ? { bundle_id: bundleIds } : {})
    };
  };

  const validateQuantity = useCallback((value, cartItem = null) => {
    if (value === "" || value === null || value === undefined) {
      showSnackbar("Please enter a valid quantity");
      return false;
    }
    const allowDecimal = cartItem ? allowsDecimalQty(cartItem) : false;
    const numValue = allowDecimal ? parseFloat(value) : Number(value);
    if (isNaN(numValue) || numValue < 0) {
      showSnackbar("Please enter a valid quantity");
      return false;
    }
    if (allowDecimal) {
      if (numValue < 0.01 || numValue > 9999.99) {
        showSnackbar("Quantity must be between 0.01 and 9999.99");
        return false;
      }
      const decimalPart = String(value).split(".")[1];
      if (decimalPart && decimalPart.length > 2) {
        showSnackbar("Quantity can have at most 2 decimal places");
        return false;
      }
    } else if (!Number.isInteger(numValue) || numValue < 1 || numValue > 9999) {
      showSnackbar("Quantity must be between 1 and 9999");
      return false;
    }
    return true;
  }, [showSnackbar]);

  const validateDiscount = useCallback((value, discountType = "percentage", cartItem = null) => {
    if (value === "" || value === null || value === undefined) {
      return true;
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
      showSnackbar(`Please enter a valid discount ${discountType === "percentage" ? "percentage" : "amount"}`);
      return false;
    }

    if (numValue < 0) {
      showSnackbar("Discount cannot be negative");
      return false;
    }

    if (discountType === "percentage") {
      if (numValue > 100) {
        showSnackbar("Discount cannot exceed 100%");
        return false;
      }
    } else if (discountType === "amount" || discountType === "£" || discountType === "fixed") {
      if (cartItem) {
        const orderType = cartItem.order_type || cartItem.type || "Box";
        let unitPrice = 0;
        if (cartItem.unit_price !== undefined && cartItem.unit_price !== null && cartItem.unit_price !== "") {
          unitPrice = parseFloat(cartItem.unit_price);
        } else if (orderType === "Box") {
          unitPrice = parseFloat(cartItem.sales_box_price || 0);
        } else {
          unitPrice = parseFloat(cartItem.sales_piece_price || 0);
        }

        if (unitPrice > 0 && numValue > unitPrice) {
          showSnackbar(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
          return false;
        }
      }
    }

    const decimalPlaces = (value.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      showSnackbar("Discount can have maximum 2 decimal places");
      return false;
    }

    return true;
  }, [showSnackbar]);

  const validatePrice = useCallback((value) => {
    if (value === "" || value === null || value === undefined) {
      showSnackbar("Please enter a valid price");
      return false;
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
      showSnackbar("Please enter a valid price");
      return false;
    }

    if (numValue < 0.01 || numValue > 99999.99) {
      showSnackbar("Price must be between £0.01 and £99,999.99");
      return false;
    }

    const decimalPlaces = (value.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      showSnackbar("Price can have maximum 2 decimal places");
      return false;
    }

    return true;
  }, [showSnackbar]);

  const handleCloseTelecallerStockQuickOrderDialog = useCallback(() => {
    setTelecallerStockDialog({
      open: false,
      productName: "",
      productCode: "",
      maxQty: 0,
      message: "",
      plainTitle: undefined,
    });
    focusQuickProductSearch();
  }, [focusQuickProductSearch]);

  const handleValidatedQuantityChange = useCallback((cartItem, value, changeType = 'absolute') => {
    const sourceItem = cartItem?.originalItem || cartItem;
    const allowDecimal = allowsDecimalQty(sourceItem);

    if (changeType !== 'increment' && changeType !== 'decrement' && !validateQuantity(value, sourceItem)) {
      return false;
    }

    const result = computeCartQuantityChange({
      sourceItem,
      validationMap: cartQtyValidationRef.current,
      value,
      changeType,
      shouldCheckStockBeforeAddToCart,
      shouldSkipStockCheck: shouldSkipTelecallerStockLimit,
      cartItems: cartItemsRef.current,
    });

    const accepted = applyCartQtyValidationResult(result, {
      onRange: (r) => {
        const msg = allowDecimal
          ? `Quantity must be between ${r.minQty} and ${r.maxQty}`
          : `Quantity must be between ${r.minQty} and ${r.maxQty}`;
        showSnackbar(msg, "warning");
      },
      onPurchaseLimit: (r) => {
        setTelecallerStockDialog({
          open: true,
          productName: getTelecallerProductDisplayName(sourceItem),
          productCode: sourceItem.product_code || sourceItem.code || "",
          maxQty: undefined,
          message: r.message || "",
          plainTitle: "Purchase Limit Exceeded",
        });
      },
      onStock: (r) => {
        setTelecallerStockDialog({
          open: true,
          productName: getTelecallerProductDisplayName(sourceItem),
          productCode: sourceItem.product_code || sourceItem.code || "",
          maxQty: r.cap,
          message: "",
          plainTitle: undefined,
        });
      },
    });

    if (!accepted) {
      return false;
    }

    cartQtyValidationRef.current.set(result.lineKey, result.finalQty);

    if (handleQuantityChange && typeof handleQuantityChange === 'function') {
      handleQuantityChange(cartItem, value, changeType);
    }
    if (!isTelecaller) {
      refocusQuickProductSearchUnlessCartQtyFocused();
    }
    return true;
  }, [
    validateQuantity,
    shouldCheckStockBeforeAddToCart,
    showSnackbar,
    handleQuantityChange,
    isTelecaller,
    refocusQuickProductSearchUnlessCartQtyFocused,
  ]);

  const handleValidatedTypeChange = useCallback((cartItem, newType) => {
    if (handleTypeChange && typeof handleTypeChange === 'function') {
      handleTypeChange(cartItem, newType);
    }
    refocusQuickProductSearchUnlessCartQtyFocused();
  }, [handleTypeChange, refocusQuickProductSearchUnlessCartQtyFocused]);

  const handleValidatedTotalInvDiscChange = (value) => {
    if (validateDiscount(value, totalInvDiscType)) {
      setTotalInvDiscPercent(value);
      focusQuickProductSearch();
    }
  };

  const handleValidatedPriceChange = useCallback((cartItem, value, options = {}) => {
    if (isRhp) {
      return false;
    }

    const itemDetails = getItemDetails ? getItemDetails(cartItem) : null;
    if (itemDetails?.isFree) {
      showSnackbar("Cannot edit price for free promotional items", "warning");
      return false;
    }

    if (options?.commit === false) {
      if (handlePriceChange && typeof handlePriceChange === 'function') {
        handlePriceChange(cartItem, value, options);
      }
      return true;
    }

    if (validatePrice(value)) {
      if (handlePriceChange && typeof handlePriceChange === 'function') {
        handlePriceChange(cartItem, value, options);
      }
      focusQuickProductSearch();
      return true;
    }

    return false;
  }, [isRhp, getItemDetails, showSnackbar, validatePrice, handlePriceChange, focusQuickProductSearch]);

  const handleValidatedDiscountChange = useCallback((cartItem, value, discountType = "percentage") => {
    if (validateDiscount(value, discountType, cartItem)) {
      if (handleDiscountChange && typeof handleDiscountChange === 'function') {
        handleDiscountChange(cartItem, value, discountType);
      }
      if (!retainsCartDiscountFocus) {
        focusQuickProductSearch();
      }
    }
  }, [validateDiscount, handleDiscountChange, focusQuickProductSearch]);

  const handleDeleteItemClick = useCallback((productId) => {
    setDeleteItemDialog({ open: true, itemId: productId });
  }, []);

  const handleConfirmDeleteItem = () => {
    if (deleteItemDialog.itemId) {
      if (handleDeleteItem && typeof handleDeleteItem === 'function') {
        handleDeleteItem(deleteItemDialog.itemId);
      }
    }
    setDeleteItemDialog({ open: false, itemId: null });
    focusQuickProductSearch();
  };

  const handleCloseDeleteItemDialog = () => {
    setDeleteItemDialog({ open: false, itemId: null });
    focusQuickProductSearch();
  };

  const handleNewOrder = () => {
    window.open(`${config.BASE_URL}/sales/quick-order`, "_blank", "noopener,noreferrer");
  };

  const handleCancel = () => {
    if (handleClearCart && typeof handleClearCart === 'function') {
      handleClearCart();
    }
    setTotalInvDiscPercent(0);
    setSelectedCustomer(null);
    setCustomerSearchInput("");
    showSnackbar("Order cancelled", "info");
  };

  const handleClearSelectedCustomerCart = () => {
    const customerId = selectedCustomer?.id;
    if (!customerId) {
      showSnackbar("Please select a customer", "warning");
      return;
    }

    clearCartMutation.mutate({
      customerId,
      platform: PLATFORMS.TELE_CALLING,
    });
  };

  const ensureStockBeforePlaceOrder = useCallback(async () => {
    if (!shouldCheckStockBeforeAddToCart) {
      return { ok: true };
    }
    try {
      const stockMap = await fetchProductStockMapForCartLines({
        cartItems,
        customerId: selectedCustomer?.id,
        platform: PLATFORMS.TELE_CALLING,
        useSearchIndex: true,
        apiBaseUrl: productSearchApiBase,
      });
      return validateCartStockBeforePlaceOrder(cartItems, stockMap, {
        shouldCheckStock: true,
        shouldSkipStockCheck: shouldSkipTelecallerStockLimit,
      });
    } catch {
      showSnackbar("Unable to verify stock availability", "error");
      return { ok: false, failedFetch: true };
    }
  }, [
    cartItems,
    selectedCustomer?.id,
    shouldCheckStockBeforeAddToCart,
    productSearchApiBase,
    showSnackbar,
  ]);

  const handlePlaceOrder = async () => {
    if (!selectedCustomer) {
      showSnackbar("Please select a customer", "warning");
      return;
    }

    if (cartItems.length === 0) {
      showSnackbar("Please add products to cart", "warning");
      return;
    }

    if (cartItems.some(cartLineMissingDiscountComment)) {
      showSnackbar("Comment is required when discount is applied", "error");
      return;
    }

    const stockCheck = await ensureStockBeforePlaceOrder();
    if (!stockCheck.ok) {
      if (stockCheck.failedFetch) return;
      setTelecallerStockDialog({
        open: true,
        productName: stockCheck.productName || "Product",
        productCode: stockCheck.productCode || "",
        maxQty: stockCheck.cap ?? 0,
        message: "",
        plainTitle: undefined,
      });
      return;
    }

    const orderPayload = buildOrderPayload();
    placeOrderMutation.mutate(orderPayload);
  };

  const handleSaveOrder = () => {
    queryClient.invalidateQueries({ queryKey: ["productList"] });
    setSelectedCustomer(null);
    setCustomerSearchInput("");
    customerSearchRef.current?.focusInput?.();
  };

  const handleCustomerChange = (newCustomer) => {
    const isCustomerDifferent =
      String(selectedCustomer?.id || "") !== String(newCustomer?.id || "");

    if (selectedCustomer && isCustomerDifferent && cartItems.length > 0) {
      setCustomerChangeDialog({
        open: true,
        newCustomer: newCustomer,
      });
      return;
    }

    setSelectedCustomer(newCustomer);
    queryClient.invalidateQueries({ queryKey: ["productList"] });
    setCustomerSearchInput(formatCustomerDisplayLabel(newCustomer));
    // if (newCustomer?.id) {
    //   setTimeout(() => {
    //     setInventoryModalOpen(true);
    //   }, 300);
    // }
  };

  const handleProceedCustomerChange = () => {
    setSelectedCustomer(customerChangeDialog.newCustomer);
    queryClient.invalidateQueries({ queryKey: ["productList"] });
    setCustomerChangeDialog({ open: false, newCustomer: null });
    focusQuickProductSearch();
  };

  const handleCancelCustomerChange = () => {
    setCustomerChangeDialog({ open: false, newCustomer: null });
    focusQuickProductSearch();
  };

  const handleInventoryProductSelect = useCallback((product) => {
    if (!product || !selectedCustomer?.id) return;

    const productId = String(product.id || "");
    const splitValue = String(product.split || "0").trim();

    let orderType = product.order_type;
    if (!orderType) {
      if (splitValue === "1") {
        orderType = "Piece";
      } else if (splitValue === "2") {
        const storedOrderType = productSplitService.getDefaultOrderType(selectedCustomer.id, productId);
        orderType = storedOrderType || "Box";
      } else {
        orderType = "Box";
      }
    }

    const existingItem = findMergeableCartLineForProductAdd(
      cartItemLookup,
      productId,
      orderType
    );

    if (existingItem) {
      const currentQty = parseInt(existingItem.qty) || 0;
      if (currentQty >= 9999) {
        showSnackbar("Quantity cannot exceed 9999. Please reduce quantity before adding more.", "warning");
        return;
      }
      if (keepQtyFocusAfterAdd) {
        addingProductFromSearchRef.current = true;
        quickProductSearchRef.current?.closeModal?.({ skipFocus: true });
        setNewlyAddedProductId(productId);
        scheduleFocusNewlyAddedQty(productId);
        return;
      }
    }

    const productWithOrderType = {
      ...product,
      order_type: orderType
    };

    const preparedProduct = prepareProductForCart(productWithOrderType);
    const incomingQty = getRequestedCartLineQuantity(preparedProduct);
    const purchaseLimit = validateMaximumOrderQuantityForCartAdd(
      productWithOrderType,
      cartItems,
      incomingQty
    );
    if (!purchaseLimit.allowed) {
      setTelecallerStockDialog({
        open: true,
        productName: getTelecallerProductDisplayName(productWithOrderType),
        productCode: product?.product_code || product?.code || "",
        maxQty: undefined,
        message: purchaseLimit.message || "",
        plainTitle: "Purchase Limit Exceeded",
      });
      return;
    }
    if (addProductToCart && typeof addProductToCart === 'function') {
      lastCartAddProductRef.current = {
        name: product?.product_name || product?.name || "",
        code: product?.product_code || product?.code || "",
      };
      addProductToCart(preparedProduct);
    }
    addingProductFromSearchRef.current = true;
    setNewlyAddedProductId(productId);
    quickProductSearchRef.current?.clear?.();

    if (keepQtyFocusAfterAdd) {
      scheduleFocusNewlyAddedQty(productId);
    } else {
      focusQuickProductSearch();
    }
  }, [selectedCustomer?.id, cartItems, cartItemLookup, showSnackbar, addProductToCart, focusQuickProductSearch, keepQtyFocusAfterAdd, scheduleFocusNewlyAddedQty]);

  const handleInventoryProductSelectForQuantity = useCallback((product) => {
    if (!product || !selectedCustomer?.id || keypadOpeningRef.current) return;

    if (keepQtyFocusAfterAdd) {
      addingProductFromSearchRef.current = true;
      quickProductSearchRef.current?.closeModal?.({ skipFocus: true });
      handleInventoryProductSelect(product);
      return;
    }

    quickProductSearchRef.current?.closeModal?.({ skipFocus: true });
    const productId = String(product.id || "");
    const splitValue = String(product.split || "0").trim();

    let defaultOrderType = product.order_type;
    if (!defaultOrderType) {
      if (splitValue === "1") {
        defaultOrderType = "Piece";
      } else if (splitValue === "2") {
        const storedOrderType = productSplitService.getDefaultOrderType(selectedCustomer.id, productId);
        defaultOrderType = storedOrderType || "Box";
      } else {
        defaultOrderType = "Box";
      }
    }

    const orderType = defaultOrderType;
    const availableQtyForType = orderType === "Piece"
      ? (parseFloat(product.availablePieceQty ?? 0) || 0)
      : (parseFloat(product.availableBoxQty ?? 0) || 0);
    if (shouldCheckStockBeforeAddToCart && availableQtyForType <= 0) {
      setTimeout(() => {
        setTelecallerStockDialog({
          open: true,
          productName: product.product_name || product.product_code || "Product",
          productCode: product.product_code || product.code || "",
          maxQty: 0,
          message: "",
          plainTitle: undefined,
        });
      }, 0);
      return;
    }

    const existingItem = findMergeableCartLineForProductAdd(
      cartItemLookup,
      productId,
      orderType
    );

    if (existingItem) {
      const currentQty = parseInt(existingItem.qty) || 0;
      if (currentQty >= 9999) {
        showSnackbar("Quantity cannot exceed 9999. Please reduce quantity before adding more.", "warning");
        return;
      }
      product.qty = String(currentQty);
      product.quantity = currentQty;
    } else {
      product.qty = "1";
      product.quantity = 1;
    }

    keypadOpeningRef.current = true;
    setSelectedProduct(null);
    quickProductSearchRef.current?.clear?.();
    setKeypadError("");
    setSelectedOrderType(defaultOrderType);
    setSelectedProductForQuantity({ ...product });

    setTimeout(() => {
      setQuantityKeypadOpen(true);
      setTimeout(() => {
        keypadOpeningRef.current = false;
      }, 300);
    }, 100);
  }, [selectedCustomer?.id, cartItems, cartItemLookup, showSnackbar, shouldCheckStockBeforeAddToCart, keepQtyFocusAfterAdd, handleInventoryProductSelect]);

  const handleQuantityConfirm = useCallback((quantity, orderType = null) => {
    const productToAdd = selectedProductForQuantity;

    if (!productToAdd) {
      setQuantityKeypadOpen(false);
      setSelectedProductForQuantity(null);
      setKeypadError("");
      focusQuickProductSearch();
      return;
    }

    const isMeats = isMeatsProduct(productToAdd);
    const numValue = isMeats ? parseFloat(quantity) : parseInt(quantity, 10);
    const minQty = isMeats ? 0.01 : 1;
    const maxQty = isMeats ? 9999.99 : 9999;

    if (isNaN(numValue) || numValue < minQty || numValue > maxQty) {
      setKeypadError(`Please enter a valid quantity (${minQty}-${maxQty})`);
      return;
    }

    const finalOrderType = orderType || selectedOrderType || productToAdd.order_type || "Box";
    const productId = String(productToAdd.id || "");
    const existingItem = findMergeableCartLineForProductAdd(
      cartItemLookup,
      productId,
      finalOrderType
    );
    const lineForLimit = existingItem
      ? existingItem?.originalItem || existingItem
      : mergeOrderLimitFieldsFromCartLines(
          { ...productToAdd, order_type: finalOrderType },
          cartItems
        );
    const allowDec = existingItem ? allowsDecimalQty(existingItem) : isMeats;
    const currentQty = existingItem ? parseQty(existingItem, existingItem.qty) : 0;
    const targetQty = existingItem ? currentQty + numValue : numValue;

    const purchaseLimit = existingItem
      ? validateMaximumOrderQuantityForLineQty(lineForLimit, cartItems, targetQty)
      : validateMaximumOrderQuantityForCartAdd(
          { ...productToAdd, order_type: finalOrderType },
          cartItems,
          numValue
        );
    if (!purchaseLimit.allowed) {
      setTelecallerStockDialog({
        open: true,
        productName: getTelecallerProductDisplayName(lineForLimit),
        productCode: productToAdd.product_code || productToAdd.code || "",
        maxQty: undefined,
        message: purchaseLimit.message || "",
        plainTitle: "Purchase Limit Exceeded",
      });
      return;
    }

    if (shouldCheckStockBeforeAddToCart) {
      if (!isQuantityWithinLineStock(lineForLimit, targetQty, allowDec)) {
        const cap = getStockCapForCartLineItem(lineForLimit);
        setTelecallerStockDialog({
          open: true,
          productName: getTelecallerProductDisplayName(lineForLimit),
          productCode: productToAdd.product_code || productToAdd.code || "",
          maxQty: cap != null ? Math.floor(cap) : 0,
          message: "",
          plainTitle: undefined,
        });
        return;
      }
    }

    if (selectedCustomer?.id && productToAdd.id) {
      const splitValue = String(productToAdd.split || "0").trim();
      if (splitValue === "2") {
        productSplitService.setDefaultOrderType(selectedCustomer.id, productToAdd.id, finalOrderType);
      }
    }

    const productWithQuantity = {
      ...productToAdd,
      quantity: numValue,
      qty: String(numValue),
      order_type: finalOrderType
    };

    setKeypadError("");
    setQuantityKeypadOpen(false);

    setTimeout(() => {
      setSelectedProductForQuantity(null);
      setSelectedOrderType("Box");
      handleInventoryProductSelect(productWithQuantity);
    }, 100);
  }, [
    selectedProductForQuantity,
    selectedOrderType,
    selectedCustomer?.id,
    cartItems,
    cartItemLookup,
    handleInventoryProductSelect,
    focusQuickProductSearch,
    shouldCheckStockBeforeAddToCart,
  ]);

  const handleQuantityKeypadClose = useCallback(() => {
    keypadOpeningRef.current = false;
    setQuantityKeypadOpen(false);
    setSelectedProductForQuantity(null);
    setKeypadError("");
    setSelectedOrderType("Box");
    focusQuickProductSearch();
  }, [focusQuickProductSearch]);

  const handleOrderTypeChange = useCallback((newOrderType) => {
    setSelectedOrderType(newOrderType);
  }, []);

  const handleOpenPromotionsModal = () => {
    setPromotionsModalOpen(true);
  };

  const handleClosePromotionsModal = () => {
    setPromotionsModalOpen(false);
    focusQuickProductSearch();
  };

  const handleOpenPromotionsModalFromCartLine = useCallback(
    (lineItem) => {
      const pid = lineItem?.product_id ?? lineItem?.productId;
      if (!selectedCustomer?.id) {
        showSnackbar("Please select a customer first", "warning");
        return;
      }
      if (pid === undefined || pid === null || String(pid).trim() === "") {
        showSnackbar("Product is required", "warning");
        return;
      }
      const label =
        lineItem?.desc ||
        lineItem?.name ||
        lineItem?.product_name ||
        lineItem?.description ||
        "";
      const productIds =
        collectProductIdsSharingPromotionWithLine(cartItemsRef.current, lineItem);
      const productIdList =
        productIds.length > 0 ? productIds : [String(pid)];
      setProductLinePromotionsCtx({ productId: productIdList, title: label, lineItem });
    },
    [selectedCustomer?.id, showSnackbar]
  );

  const handleQuantityPopupOpen = useCallback(() => {
    setNewlyAddedProductId(null);
  }, []);

  const handleQuantityPopupClose = useCallback(() => {
    if (keepQtyFocusAfterAdd) {
      focusProductSearchAfterQtyEnter();
      return;
    }
    setNewlyAddedProductId(null);
    focusQuickProductSearch();
  }, [keepQtyFocusAfterAdd, focusProductSearchAfterQtyEnter, focusQuickProductSearch]);

  const handleCloseProductLinePromotions = useCallback(() => {
    setProductLinePromotionsCtx(null);
    focusQuickProductSearch();
  }, [focusQuickProductSearch]);

  const handleOpenPromotionDrawer = (promo) => {
    setSelectedPromotion(promo);
    setPromotionDrawerOpen(true);
    setPromotionsModalOpen(false);
  };


  const handleClosePromotionDrawer = () => {
    setPromotionDrawerOpen(false);
    setSelectedPromotion(null);
    focusQuickProductSearch();
  };

  const handleRemovePromotion = (promo) => {
    if (!selectedCustomer?.id) {
      showSnackbar("Please select a customer first", "error");
      return;
    }

    const promotionId = promo?.id ? String(promo.id) : (promo?.rawData?.id ? String(promo.rawData.id) : "");

    removePromotionMutation.mutate({
      customerId: selectedCustomer.id,
      promotionId: promotionId
    });
  };

  const findPromotionForItem = (item, appliedPromotionsData) => {
    if (!item?.product_id || !appliedPromotionsData || !Array.isArray(appliedPromotionsData)) {
      return null;
    }

    const itemProductId = String(item.product_id);

    for (const promotion of appliedPromotionsData) {
      const buyProducts = promotion.buy_products || [];

      for (const buyProduct of buyProducts) {
        const productId = String(buyProduct.product_id || buyProduct.buy_product_id || buyProduct.id || "");
        if (productId && productId === itemProductId) {
          return String(promotion.id);
        }
      }
    }

    return null;
  };

  const belongsToAppliedPromotion = (item, promo) => {
    if (!item?.product_id || !promo) {
      return false;
    }

    const itemProductId = String(item.product_id);
    const rawPromo = promo.rawData || promo;
    const buyProducts = rawPromo.buy_products || promo.buyProducts || [];

    for (const buyProduct of buyProducts) {
      const productId = String(buyProduct.product_id || buyProduct.buy_product_id || buyProduct.id || "");
      if (productId && productId === itemProductId) {
        return true;
      }
    }

    return false;
  };

  const getPromotionBuyProductIdSet = (promoRaw) => {
    const set = new Set();
    if (!promoRaw) return set;
    const arr = promoRaw.buy_products || [];
    for (const bp of arr) {
      const pid = String(bp.product_id ?? bp.buy_product_id ?? bp.id ?? "").trim();
      if (pid) set.add(pid);
    }
    return set;
  };

  const promotionsShareBuyProducts = (promoA, promoB) => {
    if (!promoA || !promoB) return false;
    const a = getPromotionBuyProductIdSet(promoA);
    const b = getPromotionBuyProductIdSet(promoB);
    for (const id of a) {
      if (b.has(id)) return true;
    }
    return false;
  };

  const isPromotionOfferLineExcludedOnApply = (item, newPromotionIdStr) => {
    const itemPromoId = String(item.promotion_id ?? item.promo_id ?? item.promosId ?? "").trim();
    const linkedToNewPromo =
      newPromotionIdStr &&
      itemPromoId &&
      itemPromoId !== "0" &&
      itemPromoId === String(newPromotionIdStr);
    if (linkedToNewPromo) return false;
    const pt = String(item.promo_type ?? item.promoType ?? item.promosType ?? item.buyOrGet ?? "").trim().toUpperCase();
    if (pt === "GET" || pt === "FREE") return true;
    if (item.generated === true || item.generated === "1" || item.generated === 1) return true;
    const price = parseFloat(item.price ?? item.unit_price ?? 0);
    if ((item.is_promoted === "1" || item.is_promoted === 1) && Number.isFinite(price) && price <= 0.0001 && pt !== "BUY") return true;
    return false;
  };

  const handleApplyPromotion = async (promo) => {
    if (!selectedCustomer?.id) {
      showSnackbar("Please select a customer first", "error");
      return false;
    }

    if (cartItems.length === 0) {
      showSnackbar("Please add products to cart first", "error");
      return false;
    }

    isPromotionOperationRef.current = true;

    try {
      const storageKey = `cartManualOverrides_${selectedCustomer.id}`;
      if (typeof window !== "undefined" && storageKey) {
        localStorage.removeItem(storageKey);
      }

      const promotionId = promo?.id ? String(promo.id) : (promo?.rawData?.id ? String(promo.rawData.id) : "");
      if (!promotionId) {
        showSnackbar("Invalid promotion", "error");
        return false;
      }

      const appliedPromotionsFromAPI = cartPromotionsData?.data?.applied_promotions || [];
      const applicablePromotionsFromAPI = cartPromotionsData?.data?.applicable_promotions || [];
      const appliedPromotionsData = Array.isArray(appliedPromotionsFromAPI) && appliedPromotionsFromAPI.length > 0
        ? appliedPromotionsFromAPI
        : (Array.isArray(applicablePromotionsFromAPI) ? applicablePromotionsFromAPI.filter((p) => p.is_currently_applied === true) : []);

      const previousPromotionId = resolvePreviousPromotionIdForChange({
        targetPromotionId: promotionId,
        targetPromotion: promo,
        appliedPromotions: appliedPromotionsData,
        cartItems,
      });

      const response = await changePromotion({
        customerId: selectedCustomer.id,
        previousPromotionId,
        promotionId,
        channel: "5",
      });

      if (!response.success) {
        showSnackbar(response.message || "Failed to apply promotion", "error");
        return false;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['cartList'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['cartPromotions'], exact: false }),
      ]);

      await refetchCart();

      showSnackbar(`Promotion "${promo.title}" applied successfully`, "success");
      return true;

    } catch (error) {
      showSnackbar("Failed to apply promotion. Please try again.", "error");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['cartList'], exact: false }),
        queryClient.invalidateQueries({ queryKey: ['cartPromotions'], exact: false }),
      ]);

      await refetchCart();
      return false;
    } finally {
      setTimeout(() => {
        isPromotionOperationRef.current = false;
      }, 1000);
      focusQuickProductSearch();
    }
  };


  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return dateString;
    try {
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        const date = new Date(year, month - 1, day);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${day}-${monthNames[date.getMonth()]}`;
      }
      return dateString;
    } catch (e) {
      return dateString;
    }
  };

  const getPromotionBanner = (promotion) => {
    if (promotion.type_id === "1") return "Buy X Get Y Free";
    if (promotion.type_id === "2") return "Buy X Get Y Discount";
    if (promotion.type_id === "3") return "Buy X at Discount";
    if (promotion.type_id === "4") return "Multi Layer Special";
    if (promotion.type_id === "5") return "Group Offer";
    return promotion.type || "Special Offer";
  };

  const transformPromotionData = (promotion) => {
    const buyProducts = (promotion.buy_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
    // API returns get_products for "Buy X Get Y" promotions, but some APIs might use offer_products
    const offerProducts = (promotion.get_products || promotion.offer_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
    const allProducts = [...buyProducts, ...offerProducts];
    const typeLabel = getPromotionBanner(promotion);

    return {
      id: String(promotion.id), // Ensure ID is always a string for consistent comparison
      title: promotion.title || promotion.name || typeLabel,
      description: promotion.description || '',
      validity: promotion.start_date && promotion.end_date
        ? `${formatDate(promotion.start_date)} to ${formatDate(promotion.end_date)}`
        : 'N/A',
      products: allProducts.map(p => p.product_name || `${p.product_code} - ${p.product_name}`),
      banner: typeLabel,
      buyProducts: buyProducts,
      offerProducts: offerProducts,
      rawData: promotion
    };
  };

  const { data: cartPromotionsData, isLoading: isLoadingCartPromotions } = useQuery({
    queryKey: ['cartPromotions', selectedCustomer?.id],
    queryFn: () => getCartPromotions('5', '5', selectedCustomer?.id),
    enabled: !!selectedCustomer?.id && !isLoadingCart && !isFetchingCart && !isCartMutating,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });



  useEffect(() => {
    if (isPromotionOperationRef.current) {
      const timer = setTimeout(() => {
        isPromotionOperationRef.current = false;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [cartItems.length, selectedCustomer?.id]);

  const appliedPromotionsRaw = cartPromotionsData?.data?.applied_promotions && Array.isArray(cartPromotionsData.data.applied_promotions)
    ? cartPromotionsData.data.applied_promotions
    : [];
  const applicableForAppliedFallback = cartPromotionsData?.data?.applicable_promotions && Array.isArray(cartPromotionsData.data.applicable_promotions)
    ? cartPromotionsData.data.applicable_promotions.filter((p) => p.is_currently_applied === true)
    : [];
  const appliedPromotions = (appliedPromotionsRaw.length > 0 ? appliedPromotionsRaw : applicableForAppliedFallback).map(transformPromotionData);

  // Use applicable_promotions from cartPromotionsData instead of fetching all promotions
  const applicablePromotionsFromCart = cartPromotionsData?.data?.applicable_promotions && Array.isArray(cartPromotionsData.data.applicable_promotions)
    ? cartPromotionsData.data.applicable_promotions.map(transformPromotionData)
    : [];

  // Filter out already applied promotions from applicable promotions
  // Convert IDs to strings for consistent comparison (API returns string IDs)
  const availablePromotions = applicablePromotionsFromCart.filter(promo => {
    const appliedIds = appliedPromotions.map(p => String(p.id));
    return !appliedIds.includes(String(promo.id));
  });

  // Use total_applied and total_applicable from API response
  const promotionCount = cartPromotionsData?.data?.total_applied
    ? parseInt(cartPromotionsData.data.total_applied)
    : appliedPromotions.length;

  const totalApplicable = cartPromotionsData?.data?.total_applicable
    ? parseInt(cartPromotionsData.data.total_applicable)
    : availablePromotions.length + appliedPromotions.length;

  const productLinePromotionsFallback = useMemo(() => {
    if (!productLinePromotionsCtx?.lineItem) {
      return [];
    }
    const freshLineItem =
      resolveFreshCartLineForPromotionPopup(cartItems, productLinePromotionsCtx.lineItem) ??
      productLinePromotionsCtx.lineItem;
    return buildCartLinePromotionsFallback(
      freshLineItem,
      appliedPromotions,
      applicablePromotionsFromCart
    );
  }, [productLinePromotionsCtx, cartItems, appliedPromotions, applicablePromotionsFromCart]);

  const getAppliedPromotionForCartItem = useCallback((item) => {
    if (!item || !Array.isArray(appliedPromotions) || appliedPromotions.length === 0) {
      return null;
    }
    const rawId = item.promotion_id ?? item.promoId ?? item.promosId ?? item.promotionId;
    const idStr = rawId != null && rawId !== "" ? String(rawId).trim() : "";
    if (idStr !== "") {
      const n = Number(rawId);
      if (Number.isFinite(n) && n > 0) {
        const found = appliedPromotions.find(
          (p) =>
            Number(p?.id) === n ||
            Number(p?.rawData?.id) === n ||
            Number(p?.rawData?.promotion_id) === n
        );
        if (found) {
          return found;
        }
      }
    }
    for (const promo of appliedPromotions) {
      if (belongsToAppliedPromotion(item, promo)) {
        return promo;
      }
      const rawPromo = promo.rawData || promo;
      const getProducts =
        rawPromo.get_products ||
        rawPromo.offer_products ||
        promo.getProducts ||
        promo.offerProducts ||
        [];
      const itemPid = String(item.product_id ?? "");
      for (let i = 0; i < getProducts.length; i += 1) {
        const gp = getProducts[i];
        const pid = String(gp?.product_id ?? gp?.get_product_id ?? gp?.id ?? "");
        if (pid && pid === itemPid) {
          return promo;
        }
      }
    }
    return null;
  }, [appliedPromotions]);

  const handlePromoQtyLineOpenDrawer = useCallback((cartItem) => {
    if (!cartLineHasPromoOfferQtyControls(cartItem)) return;
    const promo = getAppliedPromotionForCartItem(cartItem);
    let pid = promo?.id != null ? Number(promo.id) : 27;
    if (!Number.isFinite(pid) || pid <= 0) {
      pid = 27;
    }
    const slabId = resolveCartLineSlabId(
      cartItem,
      bundleDiscountsRef.current,
      cartItemsRef.current,
      promo?.rawData
    );
    setPromoProductSwapCtx({ cartItem, promotionId: pid, slabId });
    setPromoProductSwapOpen(true);
  }, [getAppliedPromotionForCartItem]);

  const incompleteOrders =
    incompleteOrdersData?.data?.map((order) => ({
      id: order.id ?? order.order_id ?? null,
      customerId: order.customer_id ?? order.customerId ?? order.cust_id ?? null,
      customerName: order.name ?? order.customer_name ?? "N/A",
      date: order.created_at ?? order.created_at ?? "N/A",
      account_no: order.account_no ?? order.account_no ?? "",
      username: order.user_name
    })) || [];

  const handleIncompleteOrder = () => {
    setOpenIncompleteOrderDialog(true);
    refetchIncompleteOrders();
  };

  const handleCloseIncompleteOrderDialog = () => {
    setOpenIncompleteOrderDialog(false);
    focusQuickProductSearch();
  };

  const handleSelectIncompleteOrderCustomer = (order) => {
    const customerName = order.customerName || "";
    const accountNo = order.account_no || "";
    const customer = {
      id: order.customerId,
      company: customerName,
      name: customerName,
      account_no: accountNo,
    };

    setSelectedCustomer(customer);
    setCustomerSearchInput(formatCustomerDisplayLabel(customer));
    setOpenIncompleteOrderDialog(false);
    focusQuickProductSearch();
  };

  const [searchIncompleteOrders, setSearchIncompleteOrders] = useState("");

  const handleRequireCustomerForProductSearch = useCallback(() => {
    showSnackbar("Please select a customer first", "warning");
  }, [showSnackbar]);

  const filteredIncompleteOrders = incompleteOrders.filter((order) =>
    order.customerName
      ?.toLowerCase()
      .includes(searchIncompleteOrders.toLowerCase())
  );

  return (
    <>
      <Grid2 sx={{ p: 1, backgroundColor: "#f5f7fa" }}>
        <Grid2 container spacing={1} sx={{ overflowX: 'hidden', width: '100%', maxWidth: '100%' }}>
          <Grid2
            size={{ xs: 12, md: 12, lg: 9 }}
            bgcolor="#ffffff"
            borderRadius={2}
            sx={{ p: 1.5, overflowX: 'hidden', minWidth: 0, maxWidth: '100%', boxShadow: 2 }}
            minHeight="calc(100vh - 120px)"
          >
            <Box className="quick-order-toolbar-row" sx={{ mb: 1, width: '100%', minWidth: 0 }}>
              <Box className="quick-order-toolbar-inner">
                <Box className="quick-order-toolbar-customer quick-order-toolbar-customer-wrap">
                  <CustomerSearch
                    ref={customerSearchRef}
                    selectedCustomer={selectedCustomer}
                    onCustomerChange={handleCustomerChange}
                    searchInput={customerSearchInput}
                    onSearchInputChange={setCustomerSearchInput}
                    options={customerOptions}
                    isLoading={isLoadingCustomerList}
                    debouncedSearch={debouncedCustomerSearch}
                    onEnterKey={() => focusQuickProductSearch(true)}
                    onTabKey={() => focusQuickProductSearch(true)}
                  />
                </Box>
                <QuickOrderProductSearch
                  ref={quickProductSearchRef}
                  disabled={!selectedCustomer?.id}
                  selectedCustomer={selectedCustomer}
                  productOptions={productOptionsRaw}
                  isLoading={isLoadingProductList}
                  onProductSelect={handleInventoryProductSelectForQuantity}
                  onRequireCustomer={handleRequireCustomerForProductSearch}
                />
                <Box className="quick-order-toolbar-inline-buttons quick-order-toolbar-actions">
                  {PREFIX !== "atr" && (
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={handleIncompleteOrder}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Open Order
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    className="button-variant-outlined"
                    startIcon={<Iconify icon="mdi:note-text-outline" width={18} height={18} />}
                    onClick={() => setSageMemosPopupOpen(true)}
                    disabled={!selectedCustomer?.id}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Memos
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box sx={{ minWidth: 0, maxWidth: '100%', width: '100%' }}>
              <CartTable
                isQuickOrder={true}
                cartItems={cartItems}
                onTypeChange={handleValidatedTypeChange}
                onQuantityChange={handleValidatedQuantityChange}
                onPriceChange={!isRhp ? handleValidatedPriceChange : undefined}
                onDiscountChange={handleValidatedDiscountChange}
                onDiscountCommentChange={isTelecaller ? handleDiscountCommentChange : null}
                onDeleteItem={handleDeleteItemClick}
                isLoading={isLoadingCart}
                getItemDetails={getItemDetails}
                isItemPending={isItemPending}
                onValidationError={showSnackbar}
                customerId={selectedCustomer?.id}
                newlyAddedProductId={newlyAddedProductId}
                onQuantityPopupOpen={handleQuantityPopupOpen}
                onQuantityPopupClose={handleQuantityPopupClose}
                onPromoQtyOpenPromotionsModal={handleOpenPromotionsModalFromCartLine}
                onPromoQtyLineOpenDrawer={handlePromoQtyLineOpenDrawer}
                bundleDiscounts={bundleDiscounts}
                bundleLineDiscountByKey={bundleLineDiscountByKey}
                isTelecaller={isTelecaller}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 12, lg: 3 }} bgcolor="#ffffff" borderRadius={2} sx={{ p: 1.5, overflowX: 'hidden', minWidth: 0, maxWidth: '100%', boxShadow: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  className="button-variant-outlined"
                  startIcon={<Iconify icon="mingcute:add-line" width={16} height={16} />}
                  onClick={handleNewOrder}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }}
                >
                  New Order
                </Button>
              </Box>
              {selectedCustomer && selectedCustomer.outstanding_amount != null && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                  p: { xs: 0.75, sm: 1 },
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px'
                }}>
                  <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Account Balance
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: Number(selectedCustomer.outstanding_amount) < 0 ? '#d32f2f' : 'text.primary'
                    }}
                  >
                    {Number(selectedCustomer.outstanding_amount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mb: 1 }}>
                <Autocomplete
                  className="input-border"
                  size="small"
                  options={teleCallerOptions}
                  value={selectedTeleCaller}
                  onChange={(e, newValue) => setSelectedTeleCaller(newValue)}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select Tele Caller" />
                  )}
                />
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                backgroundColor: '#f5f7fa',
                mb: 1,
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                p: { xs: 0.75, sm: 1 }
              }}>
                {isRhp ? (
                  <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Total box - {(cartTotals?.boxCount || 0) + (cartTotals?.pieceCount || 0)}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Box Total - {cartTotals?.boxCount || 0}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Piece Total - {cartTotals?.pieceCount || 0}
                    </Typography>
                  </>
                )}
                <Button
                  variant="outlined"
                  className="button-variant-outlined"
                  size="small"
                  onClick={handleClearSelectedCustomerCart}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Clear Cart
                </Button>
              </Box>

              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: { xs: 0.75, sm: 1 }, px: { xs: 0.75, sm: 1 }, borderBottom: '1px dotted #E0E0E0' }}>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Zero VAT Goods
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {(cartTotals?.zeroGoods || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: { xs: 0.75, sm: 1 }, px: { xs: 0.75, sm: 1 }, borderBottom: '1px dotted #E0E0E0' }}>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    VAT Goods
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {(cartTotals?.stdGoods || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: { xs: 0.75, sm: 1 }, px: { xs: 0.75, sm: 1 }, borderBottom: '1px dotted #E0E0E0' }}>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    VAT
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {(cartTotals?.vat || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: { xs: 0.75, sm: 1 }, px: { xs: 0.75, sm: 1 } }}>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Grand Total
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {(cartTotals?.grandTotal || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: { xs: 0.75, sm: 1 }, display: 'flex', gap: 1 }}>
                <Button
                  sx={{
                    '&.MuiButton-root': {
                      height: { xs: '36px', sm: '40px' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }
                  }}
                  variant="outlined"
                  fullWidth
                  onClick={handleSaveOrder}
                  disabled={!selectedCustomer?.id || placeOrderMutation.isPending}
                >
                  Save Order
                </Button>
                <Button
                  sx={{
                    '&.MuiButton-root': {
                      height: { xs: '36px', sm: '40px' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      backgroundColor: '#F8991D',
                      '&:hover': {
                        backgroundColor: '#F8991D',
                        boxShadow: 'none',
                        color: '#fff',
                      }
                    }
                  }}
                  variant="contained"
                  fullWidth
                  onClick={handlePlaceOrder}
                  disabled={placeOrderMutation.isPending}
                >
                  {placeOrderMutation.isPending ? "Placing Order..." : "Place Order"}
                </Button>
              </Box>
            </Box>
            <Paper
              variant="elevation"
              elevation={0}
              sx={{
                px: { xs: 1, sm: 1.5 },
                py: { xs: 1, sm: 1.5 },
                mt: { xs: 1.5, sm: 2 },
                mb: { xs: 3, sm: 0 },
                backgroundColor: '#E3F2FD',
                borderRadius: '10px',
                border: 'none'
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 1, sm: 2 }}>
                <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ flex: 1 }}>
                  <Iconify
                    icon="ri:discount-percent-line"
                    width={32}
                    height={32}
                    sx={{ color: '#F8991D', width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                  />
                  <Typography
                    fontWeight={600}
                    sx={{ color: '#000', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  >
                    {promotionCount > 0
                      ? `${promotionCount} out of ${totalApplicable} promotion${totalApplicable !== 1 ? 's' : ''} applied!`
                      : totalApplicable > 0
                        ? `${totalApplicable} promotion${totalApplicable !== 1 ? 's' : ''} available to apply!`
                        : '0 promotions available'
                    }
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenPromotionsModal}
                  fullWidth={false}
                  sx={{
                    backgroundColor: '#F8991D',
                    color: '#fff',
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    alignSelf: { xs: 'stretch', sm: 'auto' },
                    '&:hover': {
                      // backgroundColor: '#1565C0'
                      backgroundColor: '#F8991D'
                    }
                  }}
                >
                  View
                </Button>
              </Stack>
            </Paper>
          </Grid2>
        </Grid2>
      </Grid2>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          transition: {
            style: { transformOrigin: 'top right' },
          },
        }}
        sx={(theme) => ({
          zIndex: theme.zIndex.snackbar,
          top: 24,
          right: 24,
          left: 'auto',
          bottom: 'auto',
        })}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 420 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SageCustomerMemosPopup
        open={sageMemosPopupOpen}
        onClose={() => setSageMemosPopupOpen(false)}
        customer={selectedCustomer}
      />

      <ConfirmDialog
        maxWidth="sm"
        open={customerChangeDialog.open}
        onClose={handleCancelCustomerChange}
        title="Unsaved Products in Cart"
        closeIcon
        cancelLabel="Close"
        content={
          <Typography>
            {PREFIX === "atr"
              ? "You have products in the cart that haven't been saved. Changing the customer will clear the current cart."
              : "You have products in the cart that haven't been saved. If you want to save them and continue working later, please click save order. The order will then be available in Open Orders."}
          </Typography>
        }
        action={
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#F8991D",
              "&:hover": {
                backgroundColor: "#F8991D",
                color: "#fff",
              },
            }}
            onClick={handleProceedCustomerChange}
          >
            Save Order
          </Button>
        }
      />

      <Dialog
        open={openIncompleteOrderDialog}
        onClose={handleCloseIncompleteOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Typography variant="h6">Open Order</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '400px' }}>
              <TextField
                className="input-border"
                placeholder="Search by company name"
                fullWidth
                size="small"
                value={searchIncompleteOrders}
                onChange={(e) => setSearchIncompleteOrders(e.target.value)}
              />
            </Box>
            <IconButton onClick={handleCloseIncompleteOrderDialog} size="small">
              <Iconify icon="mdi:close" width={20} height={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" className="table-cell-border">
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell sx={{ fontWeight: 600, width: '50px' }}>Sr. No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '70px' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '250px' }}>Company Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '100px' }}>Order By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIncompleteOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No open orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIncompleteOrders.map((order, index) => (
                    <TableRow key={order.id || index} hover className="table-cell">
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{order.date}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          onClick={() => handleSelectIncompleteOrderCustomer(order)}
                          sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: "none",
                            textDecoration: "underline",
                            justifyContent: "flex-start",
                          }}
                        >
                          {order.customerName} ({order.account_no})
                        </Button>
                      </TableCell>
                      <TableCell>
                        {order.username}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}>
            <Button variant="outlined" className="button-variant-outlined" onClick={handleCloseIncompleteOrderDialog}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <SalesStockLimitDialog
        open={telecallerStockDialog.open}
        onClose={handleCloseTelecallerStockQuickOrderDialog}
        productName={telecallerStockDialog.productName}
        productCode={telecallerStockDialog.productCode}
        maxQty={telecallerStockDialog.maxQty}
        message={telecallerStockDialog.message || ""}
        plainTitle={telecallerStockDialog.plainTitle}
      />

      <ConfirmDialog
        open={deleteItemDialog.open}
        onClose={handleCloseDeleteItemDialog}
        title="Delete Item"
        content={
          <>
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              Are you sure you want to remove this product from cart?
            </Typography>
            {(() => {
              const itemToDelete = cartItems.find(item => {
                const itemId = item.id || item.product_id;
                return String(itemId) === String(deleteItemDialog.itemId);
              });

              if (!itemToDelete) return null;

              const itemDetails = getItemDetails ? getItemDetails(itemToDelete) : null;
              const itemHasPromotions = itemToDelete.is_promoted === "1" ||
                itemToDelete.promo_id ||
                itemDetails?.isPromoted ||
                itemDetails?.isGetPromotion;

              const hasAnyAppliedPromotions = appliedPromotions.length > 0;

              // if (itemHasPromotions || hasAnyAppliedPromotions) {
              //   return (
              //     <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
              //       <Iconify icon="mdi:warning" width={24} height={24} sx={{ color: 'warning.main', mr: 0.5 }} /> Warning: Deleting this product may remove some promotion items that are associated with it.
              //     </Typography>
              //   );
              // }

              return null;
            })()}
          </>
        }
        action={
          <>
            <Button
              variant="contained"

              onClick={handleConfirmDeleteItem}
              sx={{
                ml: 1,
                backgroundColor: '#F8991D',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#F8991D',
                  color: '#fff'
                }
              }}
            >
              Delete
            </Button>
          </>
        }
      />

      <Dialog
        open={promotionsModalOpen}
        onClose={handleClosePromotionsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '80vh',
            m: { xs: 1, sm: 1 },

          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 700,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          px: 3,
          py: 1.5,
          mb: 2,
          borderBottom: '1px dashed #E0E0E0'
        }}>
          Promotions
          <IconButton
            onClick={handleClosePromotionsModal}
            sx={{ color: 'text.secondary' }}
            size="small"
          >
            <Iconify icon="mdi:close" width={24} height={24} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 3 }, minHeight: '400px' }}>
          <Box sx={{ mb: { xs: 2, sm: 3 }, minHeight: '200px' }}>
            <Typography fontWeight={700} sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Applied Promotions
            </Typography>
            {isLoadingCartPromotions ? (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {[1, 2].map((item) => (
                  <Grid size={{ xs: 12, md: 6 }} key={item}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        height: '100%'
                      }}
                    >
                      <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="90%" height={16} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="50%" height={16} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        <Skeleton variant="text" width="80%" height={16} />
                        <Skeleton variant="text" width="75%" height={16} />
                        <Skeleton variant="text" width="70%" height={16} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Skeleton variant="rounded" width="48%" height={32} />
                        <Skeleton variant="rounded" width="48%" height={32} />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : appliedPromotions.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>No promotions applied</Typography>
            ) : (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {appliedPromotions.map((promo) => (
                  <Grid size={{ xs: 12, md: 6 }} key={promo.id}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        position: 'relative',
                        height: '100%'
                      }}
                    >
                      <Box sx={{ pr: { xs: 3, sm: 4 } }}>
                        <Typography fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                          {promo.title}
                        </Typography>
                        <Typography sx={{ mb: 1, display: 'block', fontSize: '0.7rem' }}>
                          <span style={{ fontWeight: 600 }}>Validity:</span> {promo.validity}
                        </Typography>
                        <Typography sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                          <span style={{ fontWeight: 600 }}>Included Products:</span> ({promo.products.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2, backgroundColor: '#fcfcfc', p: 1, borderRadius: '8px' }}>
                          {promo.products.slice(0, 3).map((product, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Iconify icon="mdi:check-circle" width={16} height={16} sx={{ color: '#4CAF50' }} />
                              <Typography fontSize="0.7rem">{product}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.75, sm: 1 }, mt: 2 }}>
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={() => handleOpenPromotionDrawer(promo)}
                            sx={{
                              backgroundColor: '#424242',
                              color: '#fff',
                              textTransform: 'none',
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              px: 1.5,
                              py: { xs: 0.75, sm: 0.5 },
                              fontWeight: 500,
                              '&:hover': { backgroundColor: '#616161' }
                            }}
                          >
                            View More
                          </Button>
                          {totalApplicable > 1 ? (
                            <Button
                              variant="soft"
                              color="error"
                              size="small"
                              fullWidth
                              startIcon={<Iconify icon="mdi:delete" width={16} height={16} />}
                              onClick={() => handleRemovePromotion(promo)}
                              sx={{
                                fontWeight: 500,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                px: 1.5,
                                py: { xs: 0.75, sm: 0.5 }
                              }}
                            >
                              Remove Promotion
                            </Button>
                          ) : null}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
          <Box sx={{ minHeight: '200px' }}>
            <Typography fontWeight={700} sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Available Promotions
            </Typography>
            {isLoadingCartPromotions ? (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {[1, 2].map((item) => (
                  <Grid size={{ xs: 12, md: 6 }} key={item}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        position: 'relative',
                        height: '100%'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: { xs: '35px', sm: '40px' },
                          backgroundColor: '#ffd6dd',
                          borderRadius: '8px',
                          borderTopRightRadius: '8px',
                          borderBottomRightRadius: '8px'
                        }}
                      />
                      <Box sx={{ pr: { xs: 3, sm: 4 } }}>
                        <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width="90%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="50%" height={16} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                          <Skeleton variant="text" width="80%" height={16} />
                          <Skeleton variant="text" width="75%" height={16} />
                          <Skeleton variant="text" width="70%" height={16} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Skeleton variant="rounded" width="48%" height={32} />
                          <Skeleton variant="rounded" width="48%" height={32} />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : availablePromotions.length === 0 ? (
              <Typography sx={{ py: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>No available promotions</Typography>
            ) : (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {availablePromotions.map((promo) => (
                  <Grid size={{ xs: 12, md: 6 }} key={promo.id}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        position: 'relative',
                        height: '100%'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: { xs: '35px', sm: '40px' },
                          backgroundColor: '#ffd6dd',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderTopRightRadius: '8px',
                          borderBottomRightRadius: '8px'
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#f1031ab0',
                            fontWeight: 700,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            transform: 'rotate(90deg)',
                            whiteSpace: 'nowrap',
                            letterSpacing: '0.2px'
                          }}
                        >
                          {promo.banner}
                        </Typography>
                      </Box>
                      <Box sx={{ pr: { xs: 3, sm: 4 } }}>
                        <Typography fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                          {promo.title}
                        </Typography>
                        <Typography sx={{ mb: 1, display: 'block', fontSize: '0.7rem' }}>
                          <span style={{ fontWeight: 600 }}>Validity:</span> {promo.validity}
                        </Typography>
                        <Typography sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                          <span style={{ fontWeight: 600 }}>Included Products:</span> ({promo.products.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2, backgroundColor: '#fcfcfc', p: 1, borderRadius: '8px' }}>
                          {promo.products.slice(0, 3).map((product, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Iconify icon="mdi:check-circle" width={16} height={16} sx={{ color: '#4CAF50' }} />
                              <Typography variant="caption">{product}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.75, sm: 1 }, mt: 2 }}>
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={() => handleOpenPromotionDrawer(promo)}
                            sx={{
                              backgroundColor: '#424242',
                              color: '#fff',
                              textTransform: 'none',
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              px: 1.5,
                              py: { xs: 0.75, sm: 0.5 },
                              fontWeight: 500,
                              '&:hover': { backgroundColor: '#616161' }
                            }}
                          >
                            View More
                          </Button>
                          <Button
                            variant="soft"
                            size="small"
                            fullWidth
                            startIcon={<Iconify icon="mdi:lightning-bolt" width={16} height={16} />}
                            onClick={() => handleApplyPromotion(promo)}
                            sx={{
                              fontWeight: 500,
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              px: 1.5,
                              py: { xs: 0.75, sm: 0.5 },
                              backgroundColor: '#E8F5E9',
                              color: '#4CAF50',
                              '&:hover': {
                                backgroundColor: '#C8E6C9'
                              }
                            }}
                          >
                            Apply Promotion
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 1, sm: 2 }, mt: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={handleClosePromotionsModal}
              className="button-variant-outlined"
              sx={{
                borderColor: '#9E9E9E',
                color: '#424242',
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                '&:hover': { borderColor: '#757575' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleClosePromotionsModal}
              sx={{
                backgroundColor: '#F8991D',
                color: '#fff',
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                '&:hover': { backgroundColor: '#F8991D' }
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ProductLinePromotionsDialog
        open={Boolean(productLinePromotionsCtx)}
        onClose={handleCloseProductLinePromotions}
        productId={productLinePromotionsCtx?.productId ?? ""}
        customerId={selectedCustomer?.id}
        productTitle={productLinePromotionsCtx?.title ?? ""}
        platform="5"
        channels="5"
        cartItems={cartItems}
        lineItem={productLinePromotionsCtx?.lineItem ?? null}
        skipPromotionCheck
        fallbackPromotions={productLinePromotionsFallback}
        onApplyPromotion={handleApplyPromotion}
      />

      <PromotionDetailsDrawer
        open={promotionDrawerOpen}
        onClose={handleClosePromotionDrawer}
        promotion={selectedPromotion}
        selectedCustomer={selectedCustomer}
        platform={PLATFORMS.TELE_CALLING}
      />

      <PromotionGetProductsDialog
        open={promoProductSwapOpen}
        onClose={() => {
          setPromoProductSwapOpen(false);
          setPromoProductSwapCtx(null);
          focusQuickProductSearch();
        }}
        cartItem={promoProductSwapCtx?.cartItem ?? null}
        promotionId={promoProductSwapCtx?.promotionId ?? 27}
        slabId={promoProductSwapCtx?.slabId ?? null}
        channels={5}
        platform={PLATFORMS.TELE_CALLING}
        customerId={selectedCustomer?.id}
        localCartItems={cartItems}
        bundleDiscounts={bundleDiscounts}
        onSwapSuccess={async () => {
          await refreshCartThenPromotions();
          showSnackbar("Promotion product updated", "success");
          focusQuickProductSearch();
        }}
      />

      <NumericKeypadQuickOrder
        open={quantityKeypadOpen}
        onClose={handleQuantityKeypadClose}
        onConfirm={handleQuantityConfirm}
        initialValue={selectedProductForQuantity?.qty || selectedProductForQuantity?.quantity || 1}
        title="Enter Quantity"
        error={keypadError}
        min={selectedProductForQuantity && isMeatsProduct(selectedProductForQuantity) ? 0.01 : 1}
        max={selectedProductForQuantity && isMeatsProduct(selectedProductForQuantity) ? 9999.99 : 9999}
        canSplit={selectedProductForQuantity ? String(selectedProductForQuantity.split || "0").trim() === "2" : false}
        initialOrderType={selectedOrderType}
        onOrderTypeChange={handleOrderTypeChange}
        allowDecimal={selectedProductForQuantity ? isMeatsProduct(selectedProductForQuantity) : false}
      />
    </>
  );
};

export default QuickOrder;

